import React from 'react';
import PropTypes from 'prop-types';
import DollarIcon from '../../../icons/icon-dollar.svg';
import InfoModal from '../../InfoModal/InfoModal';
import getAddOnHeader from '../helpers/getAddOnHeader';
import createUniqueId from '../../../helpers/createUniqueId';
import AddOnIcon from '../../AddOnIcon/AddOnIcon';
import Checkbox from '../../Checkbox/Checkbox';
import OfferCard from '../../OfferCard/OfferCard';
import CustomReactMarkdown from '../../MarkdownRenderers/CustomReactMarkdown';
import AddOnEvents from '../analytics/addOnEvents';
import { modify } from '../../../helpers/classNameHelper';


const ServiceAddOn = ({
  addOn,
  addOn: {
    content: {
      disclaimer,
      icon,
      incentiveModalBody,
      incentiveModalSubtitle,
      incentiveModalTitle,
      incentivePrefix,
      incentiveTitle,
      incentiveTrigger,
      modalBody,
      modalSubtitle,
      modalTitle,
      style,
      subtitleModelTrigger,
      subtitlePrefix,
      title,
    },
    optionId,
    id,
  },
  isSelected,
  lineItemPrice,
  lineItemRebatesApplied,
  onChangeHandler,
  optionRebatesApplied,
  productPrice,
  styleFlag,
  uniqueId,
  uuid,
  isTile,
  analytics,
}) => {
  const callToActionHeader = getAddOnHeader({
    addOn,
    lineItemPrice,
    lineItemRebatesApplied,
    optionRebatesApplied,
    productPrice,
  });

  const modalStyleFlag = ['service', style, styleFlag].filter(flag => flag).join(' ');

  const labelId = callToActionHeader ? createUniqueId(id, `_label_${uuid}`) : undefined;
  const labelClass = modify('product-addon__checkbox', {
    checked: isSelected,
    style,
    styleFlag,
  });

  const showAppliedRebate = optionRebatesApplied.length > 0;
  const showRebateIncentive = incentiveModalTitle && !showAppliedRebate;

  const track = (type) => {
    // this is currently hardcoded since we don't have proper requirements on the generic use case
    if (analytics && isTile && optionId === 'installQuote' && AddOnEvents[type]) {
      analytics.trackEvent(AddOnEvents[type]);
    }
  };

  const renderAppliedRebate = () => {
    if (optionRebatesApplied.find(rebate => rebate.count > 0)) {
      return (
        <OfferCard
          icon="checkmark"
          name={addOn.content.offerName}
          title={addOn.content.offerTitle}
          description={addOn.content.offerDescription}
          styleFlag="addon"
        />
      );
    }

    return (
      <CustomReactMarkdown
        className="product-addon__ineligible--service"
        linkMeta={['allowIcon']}
        source={addOn.content.offerIneligible}
      />
    );
  };

  return (
    <>
      <Checkbox
        id={uniqueId}
        className={labelClass}
        isSelected={isSelected}
        focusRingClass="product-addon__checkbox--focused"
        onChange={onChangeHandler}
      >
        <>
          <div>
            <div className="product-addon__text">
              <div className="product-addon__text-header product-addon__text-header--service" id={labelId}>
                <AddOnIcon className="product-addon__icon product-addon__icon--service" type={icon} />
                {title}
              </div>
              <div className="info_modal--install__trigger-label">
                {subtitlePrefix}
                &nbsp;
                <InfoModal
                  styleFlag={modalStyleFlag}
                  message={modalBody}
                  contentLabel={modalTitle}
                  subheader={modalSubtitle}
                  header={modalTitle}
                  headerIcon={<AddOnIcon type={icon} />}
                  openTrack={() => track('modal')}
                  contentLinkTrack={() => track('modalLink')}
                >
                  {subtitleModelTrigger}
                </InfoModal>
              </div>
            </div>
            <div className="product-addon__subtext">
              {disclaimer}
            </div>
          </div>
        </>
      </Checkbox>
      {showRebateIncentive && (
        <div className={modify('product-addon__incentive', style, styleFlag)}>
          <DollarIcon className="product-addon__incentive-icon" />
          <div>
            <div>{incentiveTitle}</div>
            <div className="product-addon__incentive-subtext">
              {incentivePrefix}
              &nbsp;
              <InfoModal
                styleFlag={modalStyleFlag}
                message={incentiveModalBody}
                contentLabel={incentiveModalTitle}
                subheader={incentiveModalSubtitle}
                header={incentiveModalTitle}
                headerIcon={<DollarIcon />}
                openTrack={() => track('incentiveModal')}
              >
                {incentiveTrigger}
              </InfoModal>
            </div>
          </div>
        </div>
      )}
      {showAppliedRebate && renderAppliedRebate()}
    </>
  );
};

ServiceAddOn.propTypes = {
  addOn: PropTypes.shape({
    bonus: PropTypes.shape({
      rebateValue: PropTypes.number,
    }),
    content: PropTypes.shape({
      disclaimer: PropTypes.string,
      icon: PropTypes.string,
      incentiveModalBody: PropTypes.string,
      incentiveModalSubtitle: PropTypes.string,
      incentiveModalTitle: PropTypes.string,
      incentivePrefix: PropTypes.string,
      incentiveTitle: PropTypes.string,
      incentiveTrigger: PropTypes.string,
      modalBody: PropTypes.string,
      modalSubtitle: PropTypes.string,
      modalTitle: PropTypes.string,
      offerDescription: PropTypes.string,
      offerIneligible: PropTypes.string,
      offerName: PropTypes.string,
      offerTitle: PropTypes.string,
      productCardText: PropTypes.string,
      style: PropTypes.string,
      subtitleModelTrigger: PropTypes.string,
      subtitlePrefix: PropTypes.string,
      title: PropTypes.string,
    }),
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool,
  lineItemPrice: PropTypes.string,
  lineItemRebatesApplied: PropTypes.arrayOf(PropTypes.shape({})),
  onChangeHandler: PropTypes.func.isRequired,
  optionRebatesApplied: PropTypes.arrayOf(PropTypes.shape({})),
  productPrice: PropTypes.number.isRequired,
  styleFlag: PropTypes.string,
  uuid: PropTypes.string,
  uniqueId: PropTypes.string.isRequired,
};

ServiceAddOn.defaultProps = {
  isSelected: false,
  lineItemPrice: '',
  lineItemRebatesApplied: [],
  optionRebatesApplied: [],
  styleFlag: null,
  uuid: '',
};

export default ServiceAddOn;
